import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import MarketingShell from "./MarketingShell";
import { Plus, Trash, Eye } from "@phosphor-icons/react";
import SegmentBuilder, { emptyTree } from "./SegmentBuilder";

const SEG_KINDS = {
  all_contacts: { label: "All contacts", desc: "Every non-suppressed contact" },
  all_clients: { label: "All clients", desc: "Contacts synced from the Clients table" },
  all_leads: { label: "All leads", desc: "Contacts synced from CRM Leads" },
  custom_source: { label: "Custom source mix", desc: "Pick one or more specific source types" },
  filter_tree: { label: "Advanced filter", desc: "Build a tree of AND/OR conditions on any contact field" },
};

/* Map ?create=… query-param values onto a starting `segment_kind` so the
 * AudienceSourcePicker on the Campaign Editor can deep-link directly
 * into the right segment-creation flow. */
const CREATE_PARAM_TO_KIND = {
  clients:    "all_clients",
  leads:      "all_leads",
  contacts:   "all_contacts",
  filter:     "filter_tree",
  custom:     "custom_source",
};

export default function SegmentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", segment_kind: "all_clients", filter_definition: null });
  const [previewing, setPreviewing] = useState(null);

  // ?create=clients|leads|contacts|filter|custom — auto-open the create
  // modal pre-filled to the requested kind. Cleared from the URL once
  // consumed so refreshing doesn't keep re-opening it.
  useEffect(() => {
    const want = searchParams.get("create");
    if (want && CREATE_PARAM_TO_KIND[want]) {
      const kind = CREATE_PARAM_TO_KIND[want];
      setForm({
        name: "",
        segment_kind: kind,
        filter_definition: kind === "filter_tree" ? emptyTree() : null,
      });
      setCreateOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("create");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const reload = async () => {
    setLoading(true);
    try { setRows((await api.get("/marketing/segments")).data); }
    catch { toast.error("Failed to load segments"); }
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const doCreate = async () => {
    // Guard against empty / whitespace-only names. Previously this just
    // silently returned which looked like "the Create Segment button
    // does nothing" — the button is now visibly disabled when the name
    // is empty, but keep the toast as a backstop for keyboard / Enter
    // submission paths.
    if (!form.name.trim()) {
      toast.error("Give the segment a name first.");
      return;
    }
    const payload = {
      name: form.name.trim(),
      segment_kind: form.segment_kind,
      filter_definition: form.segment_kind === "filter_tree" ? form.filter_definition : null,
    };
    try {
      await api.post("/marketing/segments", payload);
      toast.success("Segment created");
      setCreateOpen(false); setForm({ name: "", segment_kind: "all_clients", filter_definition: null });
      reload();
    } catch (err) {
      // Surface the real server error if SQL or validation rejected
      // the payload — Mike used to see a generic "Create failed" which
      // didn't help diagnose "is the filter tree malformed?" etc.
      const detail = err?.response?.data?.detail || err?.message || "Create failed";
      toast.error(typeof detail === "string" ? detail : "Create failed");
    }
  };
  const doDelete = async (id) => {
    if (!window.confirm("Delete this segment?")) return;
    try { await api.delete(`/marketing/segments/${id}`); toast.success("Deleted"); reload(); }
    catch { toast.error("Delete failed"); }
  };
  const doPreview = async (id) => {
    try {
      const r = await api.get(`/marketing/segments/${id}/preview?limit=10`);
      setPreviewing({ id, total: r.data.total, items: r.data.items });
    } catch { toast.error("Preview failed"); }
  };

  return (
    <MarketingShell>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-[#525252]">Dynamic recipient groups that resolve live every time a campaign sends.</p>
        <button onClick={() => setCreateOpen(true)}
                className="h-9 px-3 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                data-testid="new-segment-btn">
          <Plus size={12} weight="bold" />New Segment
        </button>
      </div>

      <div className="bg-white border border-[#E5E5E5]">
        {loading ? <p className="p-8 text-center text-sm text-[#A3A3A3]">Loading…</p>
        : rows.length === 0 ? <p className="p-8 text-center text-sm text-[#A3A3A3]">No segments yet. Try creating "All Clients" or "All Leads".</p>
        : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left px-5 py-2 font-bold">Name</th>
                <th className="text-left px-3 py-2 font-bold">Kind</th>
                <th className="text-right px-5 py-2 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.id} className="border-t border-[#F5F5F5]" data-testid={`segment-row-${s.id}`}>
                  <td className="px-5 py-3 font-semibold text-[#0A0A0A]">{s.name}</td>
                  <td className="px-3 py-3 text-xs">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#F5F3FF] text-[#7C3AED]">
                      {SEG_KINDS[s.segment_kind]?.label || s.segment_kind}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => doPreview(s.id)} className="p-1 text-[#525252] hover:text-[#0055FF] mr-1" data-testid={`preview-${s.id}`}>
                      <Eye size={14} />
                    </button>
                    <button onClick={() => doDelete(s.id)} className="p-1 text-[#525252] hover:text-[#DC2626]" data-testid={`delete-${s.id}`}>
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setCreateOpen(false)}>
          <div className={`bg-white border border-[#E5E5E5] p-6 w-full overflow-auto max-h-[90vh] ${form.segment_kind === "filter_tree" ? "max-w-3xl" : "max-w-lg"}`} onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-black tracking-tight mb-4">New segment</h3>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] block mb-1">
              Name <span className="text-[#DC2626]">*</span>
            </label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                   onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); doCreate(); } }}
                   className="w-full h-10 px-3 border border-[#E5E5E5] mb-4"
                   placeholder="e.g. All active clients" data-testid="seg-name" autoFocus />
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] block mb-1">Kind</label>
            <div className="space-y-2 mb-4">
              {Object.entries(SEG_KINDS).map(([k, v]) => (
                <label key={k} className={`block p-3 cursor-pointer border ${form.segment_kind === k ? "border-[#0055FF] bg-[#EFF6FF]" : "border-[#E5E5E5]"}`} data-testid={`seg-kind-${k}`}>
                  <input type="radio" checked={form.segment_kind === k}
                         onChange={() => setForm({
                           ...form, segment_kind: k,
                           filter_definition: k === "filter_tree" ? (form.filter_definition || emptyTree()) : null,
                         })}
                         className="mr-2" />
                  <span className="text-sm font-semibold text-[#0A0A0A]">{v.label}</span>
                  <p className="text-xs text-[#525252] mt-0.5 ml-5">{v.desc}</p>
                </label>
              ))}
            </div>

            {form.segment_kind === "filter_tree" && (
              <div className="mb-4 border border-[#E5E5E5] p-3 bg-[#FAFAFA]">
                <SegmentBuilder tree={form.filter_definition || emptyTree()}
                                onChange={t => setForm({ ...form, filter_definition: t })} />
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={() => setCreateOpen(false)} className="h-9 px-4 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider" data-testid="seg-create-cancel">Cancel</button>
              <button onClick={doCreate}
                      disabled={!form.name.trim()}
                      className={`h-9 px-4 text-white text-xs font-bold uppercase tracking-wider transition-colors ${
                        form.name.trim()
                          ? "bg-[#0055FF] hover:bg-[#0044CC]"
                          : "bg-[#A3A3A3] cursor-not-allowed"
                      }`}
                      title={form.name.trim() ? "" : "Enter a segment name first"}
                      data-testid="seg-create-confirm">Create segment</button>
            </div>
          </div>
        </div>
      )}

      {previewing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setPreviewing(null)}>
          <div className="bg-white border border-[#E5E5E5] p-6 w-full max-w-lg max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-black tracking-tight mb-1">Segment preview</h3>
            <p className="text-xs text-[#525252] mb-3">Resolves to <strong>{previewing.total}</strong> contacts. Showing first {previewing.items.length}.</p>
            <table className="w-full text-sm">
              <tbody>
                {previewing.items.map(c => (
                  <tr key={c.id} className="border-t border-[#F5F5F5]">
                    <td className="py-1.5">{c.email}</td>
                    <td className="py-1.5 text-xs text-[#A3A3A3]">{c.full_name || c.company_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </MarketingShell>
  );
}
